import {
  HearingStageType,
  IHearingStage,
  IPreviousMeetInfoDto,
  MeetNotSummarizedReason,
} from 'Interfaces/MedicalCommittees';

interface IStepConfig {
  title: string;
  completedTitle?: string;
  futureTitle?: string;
  description: string;
  futureDescription?: string;
  externalLink?: string;
}

const NON_MEETING_STEP_CONFIGS: Record<number, IStepConfig> = {
  [HearingStageType.CommitteeDecisionApproval]: {
    title: 'אישור הפרוטוקול',
    completedTitle: 'הפרוטוקול אושר',
    description:
      'ההחלטה והפרוטוקול עברו ליו"ר הוועדות ויחידת הוועדות הרפואיות.',
    futureDescription:
      'בתום הדיון, החלטת הוועדה ופרוטוקול הדיון יעברו ליו"ר הוועדות ויחידת הוועדות הרפואיות.',
  },
  [HearingStageType.DisabilityPercentageDetermination]: {
    title: 'ההחלטה זמינה עבורך',
    futureTitle: 'הוצאת החלטה',
    description:
      'את פרוטוקול הדיון אפשר לראות כאן, ואת ההחלטה בנוגע לאחוזי הנכות שלך',
    futureDescription:
      'בשלב זה, החלטת הוועדה והפרוטוקול יעברו לקצין התגמולים ויחידת ההכרה. לאחר מכן נעדכן אותך באחוזי הנכות שלך',
  },
};

const getFollowUpMeetingDescription = (
  reason?: MeetNotSummarizedReason
): string => {
  switch (reason) {
    case MeetNotSummarizedReason.CommitteeRequestedPresence:
      return 'נקבע דיון נוסף כדי שצוות הוועדה יוכל להעריך את מצבך בצורה הטובה ביותר. נדרשת נוכחות בדיון';
    case MeetNotSummarizedReason.DifferentPanel:
    case MeetNotSummarizedReason.AdditionalDoctor:
      return 'נקבע דיון נוסף בנוכחות גורם רפואי שלא היה בדיון הקודם, כדי להעריך את מצבך בצורה מדויקת יותר';
    case MeetNotSummarizedReason.MissingMedicalDocuments:
      return 'נקבע דיון נוסף בעקבות חוסר במסמכים רפואיים. את ההשלמות הנדרשות אפשר לראות בראש העמוד';
    case MeetNotSummarizedReason.ReDiscussion:
      return 'לבקשת יו"ר הוועדות נקבע דיון נוסף. לא נדרשת נוכחות שלך, אבל אפשר להגיע לדיון';
    default:
      return '';
  }
};

const parseDateToValid = (value: unknown): Date | undefined => {
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? undefined : parsed;
  }
  return undefined;
};

const getMeetingStepText = (
  isFollowUp: boolean,
  isCompletedStep: boolean,
  reason?: MeetNotSummarizedReason
) => {
  const title = isFollowUp
    ? isCompletedStep
      ? 'התקיים דיון נוסף בוועדה'
      : 'דיון נוסף בוועדה'
    : isCompletedStep
      ? 'התקיים דיון בוועדה'
      : 'דיון בוועדה';

  const description = isFollowUp
    ? getFollowUpMeetingDescription(reason)
    : 'בשלב זה, צוות הוועדה יעריך את מצבך הרפואי בעקבות הפגיעה שהוכרה';

  return { title, description };
};

export const getCommitteeSteps = (
  stages: IHearingStage[],
  currentStageId: HearingStageType
) => {
  const lastCurrentIdx = stages.reduce(
    (lastIdx, stage, idx) =>
      stage.stageType.id === currentStageId ? idx : lastIdx,
    -1
  );

  let meetingIndex = 0;

  return stages.map((stage, index) => {
    const isCurrentStep = index === lastCurrentIdx;
    const isFutureStep = index > lastCurrentIdx;
    const isCompletedStep = index < lastCurrentIdx;

    if (stage.stageType.id === HearingStageType.Meeting) {
      const isFollowUp =
        meetingIndex > 0 && !!stage.prevMeetNotSummarizedReason;
      meetingIndex++;

      const { title, description } = getMeetingStepText(
        isFollowUp,
        isCompletedStep,
        stage.prevMeetNotSummarizedReason?.id
      );

      return {
        title,
        description,
        externalLink: 'https://shikum.mod.gov.il/recognition/committee',
        date: isFutureStep ? undefined : parseDateToValid(stage.date),
        isActive: isCurrentStep,
      };
    }

    const config = NON_MEETING_STEP_CONFIGS[stage.stageType.id];
    if (!config) {
      return {
        title: stage.stageType.name,
        description: '',
        date: isFutureStep ? undefined : parseDateToValid(stage.date),
        isActive: isCurrentStep,
      };
    }

    let title = config.title;
    let description = config.description;

    if (isFutureStep) {
      if (config.futureDescription) description = config.futureDescription;
      if (config.futureTitle) title = config.futureTitle;
    }

    if (isCompletedStep && config.completedTitle) {
      title = config.completedTitle;
    }

    return {
      title,
      description,
      externalLink: config.externalLink,
      date: isFutureStep ? undefined : parseDateToValid(stage.date),
      isActive: isCurrentStep,
    };
  });
};

export const hasValidPreviousMeeting = (
  prevMeetings?: IPreviousMeetInfoDto[]
): boolean =>
  prevMeetings?.some(
    (meeting) => meeting.isMeetSummarized || meeting.notSummarizedReason != null
  ) ?? false;

export const isAwaitingAdditionalMeeting = (
  prevMeetings?: IPreviousMeetInfoDto[],
  meetingDate?: Date
): boolean => (prevMeetings?.length ?? 0) > 0 && !meetingDate;

export const isFollowUpMeeting = (
  stages: IHearingStage[],
  prevMeetings?: IPreviousMeetInfoDto[]
): boolean => {
  const meetingStages = stages.filter(
    (stage) => stage.stageType.id === HearingStageType.Meeting
  );

  const hasReasonOnStage = meetingStages.some(
    (stage, index) => index > 0 && stage.prevMeetNotSummarizedReason != null
  );

  return hasReasonOnStage || hasValidPreviousMeeting(prevMeetings);
};
